import {
  Injector,
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectionStrategy,
  Renderer2,
  ElementRef,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';

import { E2E_ATTRIBUTES } from '@app/shared/types/enums/e2e-attributes.enum';

import { BaseComponent } from '../base-component/base.component';
import { DropdownConfig } from './dropdown-config';
import { DropdownOption } from './dropdown-option.type';
import { cloneDeep, isFunction } from 'lodash';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: '',
  standalone: false,
})
export abstract class DropdownBaseComponent<T>
  extends BaseComponent
  implements OnInit, OnDestroy
{
  @Input() public set config(config: DropdownConfig<T>) {
    if (!config) {
      return;
    }
    this.originData = cloneDeep(config.list);
    this.updateOptions(config.list, config.selectedValue, config.withDefault);
    this.onlyShowText = config.disabled;
  }
  @Input() public disabled: boolean = false;
  @Output() public onChange: EventEmitter<T> = new EventEmitter();

  public offClickListener: () => void;
  public isDropdownOpen: boolean = false;

  public e2eAttrs = E2E_ATTRIBUTES;
  public isOpen = false;

  public selectedOption: DropdownOption<T> | null = null;
  public optionsList: DropdownOption<T>[] | null = null;
  public originData: DropdownOption<T>[] | null = null;
  public onlyShowText: boolean = false;

  public constructor(
    injector: Injector,
    protected renderer: Renderer2,
    protected elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {
    super(injector);
  }

  public ngOnInit(): void {
    this.offClickListener = this.renderer.listen(
      'document',
      'click',
      (event: MouseEvent) => {
        const targetElement = event.target as HTMLElement;

        if (!this.elementRef.nativeElement.contains(targetElement)) {
          this.isDropdownOpen = false;
          this.isOpen = false;
          this.cdr.detectChanges();
        }
      },
    );
  }

  public ngOnDestroy(): void {
    if (isFunction(this.offClickListener)) {
      this.offClickListener();
    }
    this.offClickListener = null;
  }

  public toggleDropdown(): void {
    if (this.disabled) {
      return;
    }
    this.isOpen = !this.isOpen;
    this.isDropdownOpen = this.isOpen;
  }

  public onSelectOption(
    selectedOption: DropdownOption<T>,
    withoutToggle = false,
  ): void {
    this.onChange.emit(selectedOption.value);

    this.updateOptions(this.originData, selectedOption.value);

    if (withoutToggle) {
      return;
    }

    this.toggleDropdown();
  }

  private updateOptions(
    list: DropdownOption<T>[],
    selectedValue: T,
    withDefault: boolean = true,
  ): void {
    const sortedOptions = list
      .filter(Boolean)
      .map((i) => ({
        ...i,
        isSelected: i.value === selectedValue,
      }))
      .sort((option: DropdownOption<T>) =>
        option.value === selectedValue ? -1 : 1,
      );

    const isNeedSetSelected = withDefault || selectedValue;

    this.selectedOption = isNeedSetSelected ? sortedOptions[0] : null;
    this.optionsList = isNeedSetSelected
      ? sortedOptions.slice(1)
      : sortedOptions;
  }
}
